<template>
  <v-card dark flat class="submission-card">
    <div class="submission-header">
      <span class="submission-icon"><v-icon>mdi-video-account</v-icon></span>
      <div>
        <div class="eyebrow">Evaluation</div>
        <h2>Video-Abgabe</h2>
        <p>{{ instructions }}</p>
      </div>
      <span v-if="submission" class="status-badge" :class="`status-${statusKey}`">
        <v-icon>{{ statusIcon }}</v-icon
        >{{ statusLabel }}
      </span>
    </div>

    <div v-if="submission" class="submission-state">
      <div>
        <small>Eingereicht</small>
        <strong>{{ formatDate(submission.submittedAt || submission.createdAt) }}</strong>
      </div>
      <div v-if="submission.reviewedAt">
        <small>Geprüft</small>
        <strong>{{ formatDate(submission.reviewedAt) }}</strong>
      </div>
    </div>

    <div v-if="submission && submission.feedback" class="feedback" :class="{ 'needs-revision': canReplace }">
      <v-icon>{{ canReplace ? "mdi-comment-alert-outline" : "mdi-comment-check-outline" }}</v-icon>
      <div>
        <strong>Feedback der Academy-Leitung</strong>
        <p>{{ submission.feedback }}</p>
      </div>
    </div>

    <div v-if="!isLocked" class="upload-zone" :class="{ 'has-file': selectedFile }">
      <input ref="fileInput" type="file" accept="video/*,.mp4,.mov,.m4v,.webm,.avi,.3gp" class="native-input" @change="handleFileSelect" />
      <input ref="cameraInput" type="file" accept="video/*" capture="user" class="native-input" @change="handleFileSelect" />

      <template v-if="!selectedFile">
        <span class="upload-icon"><v-icon>mdi-cloud-upload-outline</v-icon></span>
        <h3>{{ canReplace ? "Überarbeitetes Video einreichen" : "Video aufnehmen oder auswählen" }}</h3>
        <p>
          MP4, MOV oder WebM bis maximal 60 Minuten und 5 GB. Das Originalvideo wird ohne Base64 und ohne lokale Komprimierung direkt zu Cloudflare
          Stream übertragen.
        </p>
        <div class="picker-actions">
          <v-btn color="#009640" depressed class="primary-action" @click="$refs.cameraInput.click()">
            <v-icon left>mdi-camera-outline</v-icon>Kamera
          </v-btn>
          <v-btn outlined color="#58d388" class="secondary-action" @click="openGalleryPicker">
            <v-icon left>mdi-folder-video-outline</v-icon>Datei wählen
          </v-btn>
        </div>
        <div v-if="error" class="upload-error selection-error">
          <v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span>
        </div>
      </template>

      <template v-else>
        <div class="file-row">
          <span><v-icon>mdi-file-video-outline</v-icon></span>
          <div>
            <strong>{{ selectedFile.name }}</strong>
            <small>{{ formatSize(selectedFile.size) }} · direkter Cloudflare-Upload</small>
          </div>
          <v-btn v-if="!isBusy" icon aria-label="Datei und Upload entfernen" :loading="discarding" :disabled="discarding" @click="discardUpload">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="optimization-note">
          <v-icon>mdi-shield-cloud-outline</v-icon>
          <span>Die App hält das Video nicht als Base64 im Arbeitsspeicher. Cloudflare übernimmt Speicherung, Aufbereitung und Streaming.</span>
        </div>

        <div v-if="isBusy || uploadProgress > 0" class="upload-progress">
          <div>
            <span>{{ uploadMessage }}</span
            ><strong>{{ Math.round(uploadProgress) }}%</strong>
          </div>
          <v-progress-linear :value="uploadProgress" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
        </div>

        <div v-if="error" class="upload-error">
          <v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span>
        </div>

        <div class="upload-actions">
          <div v-if="startingUpload" class="preparation-state">
            <v-progress-circular indeterminate color="#58d388" size="22" width="2" />
            <span>Der sichere Cloudflare-Upload wird vorbereitet.</span>
          </div>
          <div v-else-if="completing" class="completion-state">
            <v-progress-circular indeterminate color="#58d388" size="22" width="2" />
            <span>Die Übertragung ist fertig. Die Abgabe wird jetzt gespeichert.</span>
          </div>
          <v-btn
            v-else-if="uploading"
            outlined
            color="#ff7b86"
            class="secondary-action"
            :loading="discarding"
            :disabled="discarding"
            @click="cancelUpload">
            <v-icon left>mdi-close-circle-outline</v-icon>Abbrechen
          </v-btn>
          <v-btn v-else-if="error && uploadSession" color="#009640" depressed class="primary-action" :disabled="discarding" @click="resumeUpload">
            <v-icon left>mdi-reload</v-icon>Upload fortsetzen
          </v-btn>
          <v-btn v-else color="#009640" depressed class="primary-action" :disabled="discarding" @click="startUpload">
            <v-icon left>mdi-send-outline</v-icon>Video einreichen
          </v-btn>
        </div>
      </template>
    </div>

    <div v-else class="locked-note">
      <v-icon>{{ ["approved", "passed"].includes(statusKey) ? "mdi-check-decagram-outline" : "mdi-clock-check-outline" }}</v-icon>
      <span>{{ lockedMessage }}</span>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"
import { MAX_VIDEO_SIZE, uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

const VIDEO_MIME_BY_EXTENSION = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  m4v: "video/x-m4v",
  webm: "video/webm",
  avi: "video/x-msvideo",
  "3gp": "video/3gpp",
}

function getVideoMimeType(file) {
  const provided = String((file && file.type) || "")
    .trim()
    .toLowerCase()
  if (provided.startsWith("video/")) return provided
  const extension = String((file && file.name) || "")
    .split(".")
    .pop()
    .toLowerCase()
  return VIDEO_MIME_BY_EXTENSION[extension] || ""
}

export default {
  name: "LeadershipVideoSubmission",
  props: {
    moduleId: { type: String, required: true },
    moduleCompleted: { type: Boolean, default: false },
    instructions: {
      type: String,
      default: "Nimm ein kurzes Video auf und beschreibe, wie du das Gelernte in deinem Führungsalltag anwendest.",
    },
  },
  data() {
    return {
      submission: null,
      selectedFile: null,
      selectedMimeType: "",
      uploadSession: null,
      startingUpload: false,
      uploading: false,
      completing: false,
      uploadProgress: 0,
      error: "",
      abortController: null,
      discarding: false,
    }
  },
  computed: {
    isBusy() {
      return this.startingUpload || this.uploading || this.completing
    },
    statusKey() {
      return (this.submission && this.submission.status) || "submitted"
    },
    statusLabel() {
      const labels = {
        submitted: "Eingereicht",
        pending: "Eingereicht",
        in_review: "In Prüfung",
        reviewing: "In Prüfung",
        approved: "Bestanden",
        passed: "Bestanden",
        changes_requested: "Überarbeitung",
        revision_requested: "Überarbeitung",
        revision_required: "Überarbeitung",
        rejected: "Überarbeitung",
      }
      return labels[this.statusKey] || "Eingereicht"
    },
    statusIcon() {
      if (["approved", "passed"].includes(this.statusKey)) return "mdi-check-circle-outline"
      if (["changes_requested", "revision_requested", "revision_required", "rejected"].includes(this.statusKey)) return "mdi-reload"
      return "mdi-clock-outline"
    },
    canReplace() {
      return ["changes_requested", "revision_requested", "revision_required", "rejected"].includes(this.statusKey)
    },
    isLocked() {
      return Boolean(this.submission && !this.canReplace)
    },
    uploadMessage() {
      if (this.completing) return "Abgabe wird gespeichert"
      if (!this.uploading && this.uploadProgress >= 100) return "Übertragung abgeschlossen"
      return "Video wird direkt zu Cloudflare übertragen"
    },
    lockedMessage() {
      return ["approved", "passed"].includes(this.statusKey)
        ? this.moduleCompleted
          ? "Deine Video-Evaluation ist bestanden und das Modul ist abgeschlossen."
          : "Deine Video-Evaluation ist bestanden. Schliesse noch die übrigen Prüfungsteile und offenen Lektionen ab."
        : "Deine Abgabe ist eingereicht. Du wirst benachrichtigt, sobald Feedback vorliegt."
    },
  },
  created() {
    this.uploadDisposed = false
    this.uploadTask = null
  },
  mounted() {
    this.loadSubmission()
  },
  beforeDestroy() {
    this.uploadDisposed = true
    if (this.abortController) this.abortController.abort()
    if (this.uploadSession && this.uploadSession.streamUid && !this.completing) {
      api.akademie.cancelLeadershipSubmissionUpload(this.moduleId, this.uploadSession.streamUid, this.uploadSession.sessionToken).catch(() => {})
    }
  },
  methods: {
    async loadSubmission() {
      try {
        const result = await api.akademie.fetchLeadershipSubmission(this.moduleId)
        this.submission = result && (result.submission || (Array.isArray(result) ? result[0] : result))
      } catch (error) {
        if (!error.response || error.response.status !== 404) console.error("Video-Abgabe konnte nicht geladen werden", error)
      }
    },
    openGalleryPicker() {
      if (this.isBusy || this.discarding || !this.$refs.fileInput) return
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ""
      if (!file) return
      const mimeType = getVideoMimeType(file)
      if (!mimeType) {
        this.error = "Bitte wähle eine unterstützte Videodatei aus."
        return
      }
      if (!file.size) {
        this.error = "Die gewählte Videodatei ist leer."
        return
      }
      if (file.size > MAX_VIDEO_SIZE) {
        this.error = "Das Video ist grösser als 5 GB. Bitte kürze oder komprimiere es und versuche es erneut."
        return
      }
      this.selectedFile = file
      this.selectedMimeType = mimeType
      this.uploadSession = null
      this.uploadProgress = 0
      this.error = ""
    },
    async startUpload() {
      if (!this.selectedFile || this.isBusy || this.discarding) return
      this.startingUpload = true
      this.error = ""
      try {
        this.uploadSession = await api.akademie.startLeadershipSubmissionUpload(this.moduleId, {
          fileName: this.selectedFile.name,
          mimeType: this.selectedMimeType,
          fileSize: this.selectedFile.size,
        })
        if (this.uploadDisposed) return
        if (!this.uploadSession || !this.uploadSession.streamUid || !this.uploadSession.uploadUrl || !this.uploadSession.sessionToken) {
          throw new Error("Der Cloudflare-Upload konnte nicht vorbereitet werden.")
        }
      } catch (error) {
        this.handleUploadError(error)
        return
      } finally {
        this.startingUpload = false
      }
      await this.runUpload()
    },
    async resumeUpload() {
      if (!this.uploadSession) return this.startUpload()
      await this.runUpload()
    },
    async runUpload() {
      if (!this.selectedFile || !this.uploadSession || this.isBusy || this.discarding) return
      const task = this.uploadDirect()
      this.uploadTask = task
      try {
        await task
      } finally {
        if (this.uploadTask === task) this.uploadTask = null
      }
    },
    async uploadDirect() {
      this.uploading = true
      this.completing = false
      this.error = ""
      this.abortController = new AbortController()
      try {
        await uploadCloudflareMedia(this.selectedFile, this.uploadSession, {
          signal: this.abortController.signal,
          onProgress: (loaded, total) => {
            this.uploadProgress = Math.min(100, (loaded * 100) / Math.max(1, total || this.selectedFile.size))
          },
        })
        if (this.uploadDisposed || this.abortController.signal.aborted) return
        this.uploading = false
        this.completing = true
        this.uploadProgress = 100
        const result = await api.akademie.completeLeadershipSubmissionUpload(this.moduleId, this.uploadSession.streamUid, {
          sessionToken: this.uploadSession.sessionToken,
        })
        this.submission = result && (result.submission || result)
        this.$emit("submitted", this.submission)
        this.resetUpload()
      } catch (error) {
        if (!this.isUploadCanceled(error)) this.handleUploadError(error)
      } finally {
        this.uploading = false
        this.completing = false
        this.abortController = null
      }
    },
    async cancelUpload() {
      if (this.startingUpload || this.completing) return
      await this.discardUpload()
    },
    async discardUpload() {
      if (this.discarding || this.startingUpload || this.completing) return
      this.discarding = true
      const activeTask = this.uploadTask
      const streamUid = this.uploadSession && this.uploadSession.streamUid
      if (this.abortController) this.abortController.abort()
      try {
        if (activeTask) await activeTask
        if (streamUid) {
          await api.akademie.cancelLeadershipSubmissionUpload(this.moduleId, streamUid, this.uploadSession.sessionToken)
        }
      } catch (error) {
        const status = error.response && error.response.status
        if (![404, 410].includes(status)) console.warn("Cloudflare-Upload konnte nicht vollständig verworfen werden", error)
      } finally {
        this.resetUpload()
        this.discarding = false
      }
    },
    resetUpload() {
      this.selectedFile = null
      this.selectedMimeType = ""
      this.uploadSession = null
      this.uploadProgress = 0
      this.startingUpload = false
      this.uploading = false
      this.completing = false
      this.error = ""
    },
    handleUploadError(error) {
      const responseData = error.response && error.response.data
      if (responseData && responseData.submission) {
        this.submission = responseData.submission
        this.resetUpload()
        return
      }
      this.error =
        (responseData && (responseData.error || responseData.message)) || error.message || "Der Upload wurde unterbrochen. Du kannst ihn fortsetzen."
    },
    isUploadCanceled(error) {
      return Boolean(
        (this.abortController && this.abortController.signal.aborted) ||
          (error && (error.__CANCEL__ || error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED")),
      )
    },
    formatSize(bytes) {
      if (!bytes) return "0 MB"
      if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    formatDate(date) {
      if (!date) return "-"
      return new Date(date).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" })
    },
  },
}
</script>

<style scoped>
.submission-card {
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem !important;
  color: #f4f4f5;
  background: radial-gradient(circle at 100% 0, rgba(147, 51, 234, 0.1), transparent 15rem), rgba(255, 255, 255, 0.035) !important;
  font-size: 1rem;
}

.submission-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.875rem;
}

.submission-icon,
.upload-icon {
  display: grid;
  place-items: center;
  border-radius: 0.9375rem;
  color: #c084fc;
  background: rgba(147, 51, 234, 0.14);
}

.submission-icon {
  width: 3rem;
  height: 3rem;
}

.submission-icon .v-icon,
.upload-icon .v-icon,
.status-badge .v-icon {
  color: currentColor;
}

.eyebrow {
  color: #c084fc;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.submission-header h2,
.upload-zone h3 {
  margin: 0.1875rem 0 0.375rem;
  color: #ffffff;
  font-size: 1.25rem;
  line-height: 1.35;
}

.submission-header p,
.upload-zone > p {
  margin: 0;
  color: #b8b8bd;
  font-size: 0.875rem;
  line-height: 1.55;
}

.submission-header p {
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 2rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.14);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-badge .v-icon {
  font-size: 1rem;
}

.status-approved,
.status-passed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.status-changes_requested,
.status-revision_requested,
.status-revision_required,
.status-rejected {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.14);
}

.submission-state {
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.12);
}

.submission-state div {
  display: grid;
  gap: 0.125rem;
}

.submission-state small {
  color: #919198;
  font-size: 0.75rem;
}

.submission-state strong {
  color: #e4e4e7;
  font-size: 0.875rem;
}

.feedback,
.optimization-note,
.upload-error,
.locked-note,
.preparation-state,
.completion-state {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.feedback {
  margin-top: 0.875rem;
  padding: 0.875rem;
  border: 1px solid rgba(0, 150, 64, 0.25);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.08);
}

.feedback.needs-revision {
  border-color: rgba(244, 63, 94, 0.3);
  background: rgba(244, 63, 94, 0.08);
}

.feedback .v-icon {
  color: #7ee5a6;
}

.feedback.needs-revision .v-icon {
  color: #ff9aa5;
}

.feedback strong,
.feedback p {
  font-size: 0.875rem;
}

.feedback p {
  margin: 0.25rem 0 0;
  color: #c5c5ca;
  line-height: 1.5;
  white-space: pre-wrap;
}

.upload-zone {
  margin-top: 1rem;
  padding: 1.5rem;
  border: 1px dashed rgba(88, 211, 136, 0.35);
  border-radius: 1rem;
  text-align: center;
  background: rgba(0, 150, 64, 0.04);
}

.upload-zone.has-file {
  padding: 1rem;
  text-align: left;
}

.native-input {
  display: none;
}

.upload-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.upload-icon .v-icon {
  font-size: 1.75rem;
}

.picker-actions,
.upload-actions {
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  margin-top: 1rem;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.file-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.file-row > span {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #c084fc;
  background: rgba(147, 51, 234, 0.14);
}

.file-row > span .v-icon {
  color: currentColor;
}

.file-row div {
  display: grid;
  min-width: 0;
}

.file-row strong,
.file-row small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-row strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.file-row small {
  color: #a3a3a8;
  font-size: 0.75rem;
}

.optimization-note,
.upload-error,
.locked-note {
  margin-top: 0.875rem;
  padding: 0.75rem 0.875rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.45;
}

.optimization-note {
  color: #b7e7ca;
  background: rgba(0, 150, 64, 0.1);
}

.optimization-note .v-icon {
  color: #7ee5a6;
}

.upload-error {
  color: #ffc1c7;
  background: rgba(244, 63, 94, 0.11);
}

.upload-error .v-icon {
  color: #ff8d98;
}

.selection-error {
  max-width: 40rem;
  margin-right: auto;
  margin-left: auto;
  text-align: left;
}

.upload-progress {
  margin-top: 1rem;
}

.upload-progress > div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.375rem;
  color: #c8c8cd;
  font-size: 0.875rem;
}

.preparation-state,
.completion-state {
  align-items: center;
  color: #c8c8cd;
  font-size: 0.875rem;
}

.completion-state .v-icon {
  color: #7ee5a6;
}

.locked-note {
  color: #c9c9ce;
  background: rgba(255, 255, 255, 0.045);
}

.locked-note .v-icon {
  color: #7ee5a6;
}

@media (max-width: 600px) {
  .submission-card {
    padding: 1rem;
  }

  .submission-header {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .status-badge {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .submission-state,
  .picker-actions,
  .upload-actions {
    flex-direction: column;
  }

  .picker-actions .v-btn,
  .upload-actions .v-btn {
    width: 100%;
  }

  .upload-zone {
    padding: 1.125rem;
  }
}
</style>
